import { Component, DoCheck, HostListener, OnInit } from "@angular/core";
import { OnPremStorageService } from "../../../services/onprem-storage";
import { CloudStorageService } from "../../../services/cloud-storage";
import { SiteParameterService } from "../../../services/siteparameter.service";
import { Router } from '@angular/router';
import { Observable, Subscription } from "rxjs";
import { Profile } from "../../../models/profile";
import { KPI } from "../../../models/kpi";
import { ColDef, ColSpanParams, GridApi } from 'ag-grid-enterprise';
import { finalize, map, take } from "rxjs/operators";

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
})
export class DashboardComponent implements OnInit, DoCheck {

  public kpis$?: Observable<KPI[]>;
  public asOfDate?: string;
  public profile: Profile = <Profile>{};
  private channelUpdate: Subscription;
  public screenWidthForSmallDevice: number = 576;
  private gridApi?: GridApi;
  private gridColumnApi: any;
  public noRowsTemplate: string = "";
  public groupDefaultExpanded = 1;
  public groupRowHeight = 30;
  public rowHeight = 33;
  public screenWidthForDesktops: number = 1920;
  public innerWidth: any;
  kpiColumnDefs!: ColDef[];

  
  getRowHeight = (params:any) => {
    return params.node.group ? this.groupRowHeight : this.rowHeight; // 30px for group rows, 32px for others
  };

  public defaultColumnDef = {
    sortable: true,
    resizable: true,
  };

  autoGroupColumnDef: ColDef = {
    headerName: '',
    cellRendererParams: {
      suppressCount: true,
    },
    maxWidth: 20,
    colSpan: (params: ColSpanParams) => {
      if (params.data) {
        return 1;
      } else {
        return 4;
      }
    },
  };

  constructor(private onPremService: OnPremStorageService, public cloudStorageService: CloudStorageService, private siteParmService: SiteParameterService,
    private router: Router) {
    this.noRowsTemplate = this.siteParmService.getGridNoAvaiableKPIs();
    this.channelUpdate = this.siteParmService.getChannelUpdate().subscribe
      (message => {
        this.profile = this.siteParmService.getStaticProfile();
        this.getKpis();
      });
  }

  ngOnInit(): void {
    this.innerWidth = window.innerWidth;
    this.getKpiListColumnDefs();
    this.profile = this.siteParmService.getStaticProfile();
    this.getKpis();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.innerWidth = window.innerWidth;
    this.getKpiListColumnDefs();
  }

  getKpiListColumnDefs(): void {
    if (this.innerWidth >= this.screenWidthForDesktops) {
      this.kpiColumnDefs = [
        {
          field: "channelName", rowGroup: true, hide: true, headerName: 'Channel',
        },
        {
          minWidth: 50, maxWidth: 80, cellStyle: { textAlign: 'center' },
          cellRenderer: (params: any) => {
            return this.getGridIcons(params.data);
          },
        },
        {
          minWidth: 300,
          cellRenderer: (params: any) => {
            return this.getSubText(params);
          }
        },
        { minWidth: 60, maxWidth: 180, field: 'kpiDisplayValue', headerName: 'Value' }
      ];
    }
    else if (this.innerWidth >= this.screenWidthForSmallDevice) {
      this.kpiColumnDefs = [
        {
          field: "channelName", rowGroup: true, hide: true, headerName: 'Channel',
        },
        {
          minWidth: 30, maxWidth: 80, cellStyle: { textAlign: 'center' },
          cellRenderer: (params: any) => {
            return this.getGridIcons(params.data);
          },
        },
        {
          minWidth: 150,
          cellRenderer: (params: any) => {
            return this.getSubText(params);
          }
        },
        { minWidth: 60, maxWidth: 180, field: 'kpiDisplayValue', headerName: 'Value' }
      ];
    }
    else {
      this.kpiColumnDefs = [
        {
          field: "channelName", rowGroup: true, hide: true, headerName: 'Channel',
        },
        {
          minWidth: 30, maxWidth: 80, cellStyle: { textAlign: 'center' },
          cellRenderer: (params: any) => {
            return this.getGridIcons(params.data);
          },
        },
        { minWidth: 150, field: 'description' },
        { minWidth: 60, maxWidth: 180, field: 'kpiDisplayValue', headerName: 'Value' }
      ];
    }
  }

  getKpis(): void {

    let limitedAccessOnly = this.profile.status == 'Limited';

    this.kpis$ = this.onPremService.getDashboardKpis(this.profile.defaultAgentNumber, this.profile.channelPreferenceValue, limitedAccessOnly)
      .pipe(
        map(response => {
          this.asOfDate = response.data.kpis[0].asOfDate;
          return response.data.kpis;
        }),
        finalize(() => {
          setTimeout(() => {
            this.gridApi?.sizeColumnsToFit();
          }, 100);
        })
    );
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridApi!.setDomLayout("autoHeight");
    this.gridColumnApi = params.columnApi;
    params.api.sizeColumnsToFit();
  }

  goToBriefcase(): void {
    this.router.navigate(['/briefcase']);
  }

  getGridIcons(kpi: any): string {
    return `<i class="fad fa-xl ${kpi.icon} mr-2" style="--fa-primary-color: ${kpi.iconPrimaryColor}; --fa-secondary-color: ${kpi.iconSecondaryColor };" aria-hidden="true"></i>`;
  }

  getSubText(params: any): string {
    let returnText = "";
    if (params.data != undefined) {
      returnText = params.data.subText === '' ? params.data.description :
        `<i class="fas fa-info-circle" style="margin-right: 5px; color: #39519f; cursor: pointer;" title="${params.data.subText}"></i>${params.data.description}`;
    }
    return returnText;
  }

  ngDoCheck(): void {
    if (!this.profile) {
      this.profile = this.siteParmService.getStaticProfile();
      this.getKpis();
    }
  }

  ngOnDestroy() { // It's a good practice to unsubscribe to ensure no memory leaks
    this.channelUpdate.unsubscribe();
  }
}
